import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useAuth } from '@/stores/auth'
import { useUI } from '@/stores/ui'
import { classesApi, type ClassResponse } from '@/api/endpoints/classes'
import { parentsApi } from '@/api/endpoints/parents'
import { teachersApi } from '@/api/endpoints/teachers'
import { studentsApi } from '@/api/endpoints/students'
import { 
  openEmailClientWithRecipients, 
  getGenericAnnouncementEmailTemplate,
  getClassSpecificAnnouncementEmailTemplate 
} from '@/utils/email'
import Spinner from '@/components/common/Spinner'

interface Props {
  userRole?: string
}

export default function AutoEmailButtons({ userRole: _userRole }: Props) {
  const { user } = useAuth()
  const addToast = useUI((state) => state.addToast)
  
  if (!user) return null
  
  const [loading, setLoading] = useState(false)
  const [showClassModal, setShowClassModal] = useState(false)
  const [availableClasses, setAvailableClasses] = useState<ClassResponse[]>([])
  const [selectedClasses, setSelectedClasses] = useState<Set<string>>(new Set())
  const [loadingClasses, setLoadingClasses] = useState(false)

  // Load available classes for class-specific announcement
  useEffect(() => {
    let mounted = true
    const loadClasses = async () => {
      if (!showClassModal) return
      setLoadingClasses(true)
      try {
        const classes = await classesApi.list()
        if (mounted) {
          setAvailableClasses(Array.isArray(classes) ? classes : [])
        }
      } catch (error) {
        console.error('Failed to load classes:', error)
        if (mounted) {
          addToast({ message: 'Failed to load classes', type: 'error' })
        }
      } finally {
        if (mounted) setLoadingClasses(false)
      }
    }
    loadClasses()
    return () => { mounted = false }
  }, [showClassModal, addToast])

  const handleGenericAnnouncement = async () => {
    if (!user) return
    setLoading(true)
    try {
      // Fetch all parents and teachers
      const [parents, teachers] = await Promise.all([
        parentsApi.list(),
        teachersApi.list(),
      ])

      // Extract parent emails (only approved parents with emails)
      const parentEmails = (Array.isArray(parents) ? parents : [])
        .map(p => p.email)
        .filter((email): email is string => !!email && email.trim() !== '')

      // Extract teacher emails
      const teacherEmails = (Array.isArray(teachers) ? teachers : [])
        .map(t => t.email)
        .filter((email): email is string => !!email && email.trim() !== '')

      if (parentEmails.length === 0 && teacherEmails.length === 0) {
        addToast({ 
          message: 'No parent or teacher emails found', 
          type: 'warning' 
        })
        return
      }

      // Create email template
      const template = getGenericAnnouncementEmailTemplate({
        senderTitle: user.title || undefined,
        senderFirstName: user.first_name || '',
        senderLastName: user.last_name || '',
      })

      // Open email client with BCC for parents, CC for teachers
      openEmailClientWithRecipients(parentEmails, teacherEmails, template)
      
      addToast({ 
        message: `Email client opened with ${parentEmails.length} parent(s) in BCC and ${teacherEmails.length} teacher(s) in CC`, 
        type: 'success' 
      })
    } catch (error) {
      console.error('Failed to generate generic announcement email:', error)
      addToast({ 
        message: 'Failed to generate email. Please try again.', 
        type: 'error' 
      })
    } finally {
      setLoading(false)
    }
  }

  const handleClassSpecificAnnouncement = async () => {
    if (!user || selectedClasses.size === 0) {
      addToast({ message: 'Please select at least one class', type: 'warning' })
      return
    }

    setLoading(true)
    try {
      const selectedClassIds = Array.from(selectedClasses)
      
      // Get all students
      const allStudents = await studentsApi.list()
      
      // Filter students by selected classes
      const studentsInClasses = (Array.isArray(allStudents) ? allStudents : [])
        .filter(s => s.class_id && selectedClassIds.includes(s.class_id))

      // Get unique parent emails from students in selected classes
      // We'll fetch parent contact info for each student
      const parentEmailSet = new Set<string>()
      
      // Fetch parent contacts for students (limit to first 50 for performance)
      const studentsToFetch = studentsInClasses.slice(0, 50)
      const parentContactPromises = studentsToFetch.map(student => 
        studentsApi.getParentContact(student.id).catch(() => null)
      )
      
      const parentContacts = await Promise.all(parentContactPromises)
      
      // Extract unique parent emails
      parentContacts.forEach(contact => {
        if (contact?.parent_email) {
          parentEmailSet.add(contact.parent_email)
        }
      })

      // If we hit the limit, show a warning
      if (studentsInClasses.length > 50) {
        addToast({ 
          message: `Note: Only including parents for the first 50 students. Total students in selected classes: ${studentsInClasses.length}`, 
          type: 'warning' 
        })
      }

      // Get all teachers
      const allTeachers = await teachersApi.list()
      const teacherEmails = (Array.isArray(allTeachers) ? allTeachers : [])
        .map(t => t.email)
        .filter((email): email is string => !!email && email.trim() !== '')

      const parentEmails = Array.from(parentEmailSet)

      // Get class names for the email body
      const classNames = availableClasses
        .filter(c => selectedClassIds.includes(c.id))
        .map(c => c.name)

      if (parentEmails.length === 0 && teacherEmails.length === 0) {
        addToast({ 
          message: 'No parent or teacher emails found for selected classes', 
          type: 'warning' 
        })
        return
      }

      // Create email template with class names
      const template = getClassSpecificAnnouncementEmailTemplate({
        classNames,
        senderTitle: user.title || undefined,
        senderFirstName: user.first_name || '',
        senderLastName: user.last_name || '',
      })

      // Open email client
      openEmailClientWithRecipients(parentEmails, teacherEmails, template)
      
      addToast({ 
        message: `Email client opened for ${classNames.length} class(es) with ${parentEmails.length} parent(s) in BCC and ${teacherEmails.length} teacher(s) in CC`, 
        type: 'success' 
      })
      
      // Close modal and reset selection
      setShowClassModal(false)
      setSelectedClasses(new Set())
    } catch (error) {
      console.error('Failed to generate class-specific announcement email:', error)
      addToast({ 
        message: 'Failed to generate email. Please try again.', 
        type: 'error' 
      })
    } finally {
      setLoading(false)
    }
  }

  const toggleClassSelection = (classId: string) => {
    setSelectedClasses(prev => {
      const next = new Set(prev)
      if (next.has(classId)) {
        next.delete(classId)
      } else {
        next.add(classId)
      }
      return next
    })
  }

  return (
    <>
      <div className="mb-4 p-4 bg-gradient-to-br from-yellow-50 to-white rounded-xl border-2 border-yellow-200 shadow-sm">
        <h3 className="text-sm font-bold text-yellow-900 mb-3">📧 Auto-Email Templates</h3>
        <p className="text-xs text-gray-600 mb-3">
          Click the buttons below to open your email client with pre-filled announcement templates.
        </p>
        <div className="flex flex-wrap gap-2">
          <button
            className="btn-primary text-xs focus-ring"
            onClick={handleGenericAnnouncement}
            disabled={loading}
            title="Open email client with pre-filled parish-wide announcement. BCCs all parents, CCs all teachers."
            aria-label="Open generic announcement email"
          >
            {loading ? 'Loading...' : '📢 Generic Announcement'}
          </button>
          <button
            className="btn-primary text-xs focus-ring"
            onClick={() => setShowClassModal(true)}
            disabled={loading}
            title="Open email client with pre-filled class-specific announcement. BCCs parents of selected classes, CCs all teachers."
            aria-label="Open class-specific announcement email"
          >
            📚 Class-Specific Announcement
          </button>
        </div>
      </div>

      {/* Class Selection Modal */}
      <AnimatePresence>
        {showClassModal && (
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                setShowClassModal(false)
                setSelectedClasses(new Set())
              }
            }}
          >
            <motion.div
              className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl p-6 hover-raise mx-4 max-h-[90vh] overflow-y-auto"
              initial={{ scale: 0.96, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.96, opacity: 0 }}
              transition={{ type: 'spring', stiffness: 260, damping: 22 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4 pb-4 border-b border-gray-200">
                <h3 className="text-xl font-bold text-gray-900">Select Classes for Announcement</h3>
                <button
                  className="btn-secondary text-sm focus-ring"
                  onClick={() => {
                    setShowClassModal(false)
                    setSelectedClasses(new Set())
                  }}
                  aria-label="Close class selection"
                >
                  ✕ Close
                </button>
              </div>

              {loadingClasses ? (
                <div className="flex items-center justify-center py-12">
                  <Spinner label="Loading classes..." />
                </div>
              ) : availableClasses.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <p className="text-sm">No classes available</p>
                </div>
              ) : (
                <>
                  <div className="mb-4">
                    <p className="text-sm text-gray-600 mb-3">
                      Select one or more classes. The email will be sent to parents of students in these classes.
                    </p>
                    <div className="space-y-2 max-h-[400px] overflow-y-auto border-2 border-gray-200 rounded-lg p-3">
                      {availableClasses.map(cls => (
                        <label
                          key={cls.id}
                          className="flex items-center gap-3 p-3 hover:bg-gray-50 rounded-lg cursor-pointer transition-colors"
                        >
                          <input
                            type="checkbox"
                            checked={selectedClasses.has(cls.id)}
                            onChange={() => toggleClassSelection(cls.id)}
                            className="w-4 h-4 text-yellow-500 focus-ring rounded border-gray-300"
                          />
                          <div className="flex-1">
                            <div className="font-medium text-sm text-gray-900">{cls.name}</div>
                            {cls.weekly_time_desc && (
                              <div className="text-xs text-gray-500">{cls.weekly_time_desc}</div>
                            )}
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                    <div className="text-sm text-gray-600">
                      {selectedClasses.size > 0 && (
                        <span className="font-medium text-gray-900">
                          {selectedClasses.size} class{selectedClasses.size !== 1 ? 'es' : ''} selected
                        </span>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <button
                        className="btn-secondary text-sm focus-ring"
                        onClick={() => {
                          setShowClassModal(false)
                          setSelectedClasses(new Set())
                        }}
                      >
                        Cancel
                      </button>
                      <button
                        className="btn-primary text-sm focus-ring"
                        onClick={handleClassSpecificAnnouncement}
                        disabled={loading || selectedClasses.size === 0}
                      >
                        {loading ? 'Generating...' : '📧 Generate Email'}
                      </button>
                    </div>
                  </div>
                </>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  )
}

